import React, { useEffect, useRef, useState } from "react";
import DefaultSelect from "./form/DefaultSelect";
import { Modal, Radio } from "antd";
import DefaultDatePicker from "./form/DefaultDatePicker";
import { FilterSectionType } from "../types/FilterSectionType";
import useLang from "../hooks/useLang";
import DefaultInput from "./form/DefaultInput";
import { fetchStatusCached, fetchZonesCached } from "../lib/cachedLookups";
import api from "../utils/axios";
import classes from "./FilterOrderSection.module.css";
import { ReactComponent as CodeIcon } from "../assets/codeinput.svg";
import { ReactComponent as SearchIcon } from "../assets/search-find.svg";
import { getUserRoleFromToken } from "../utils/Auth";
import { toast } from "react-toastify";
import { orderStatuses, userRoles } from "../types/enums";
import Buttons from "./form/Buttons";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { hasAccess } from "../lib/MenuValidation";

interface Zone {
	id: number;
	description: string;
	levelId: number;
	parentId: number;
}

interface Status {
	id: number;
	description: string;
}

interface Seller {
	id: number;
	firstName: string;
	lastName: string;
	username: string;
	uniqueSellerIdentifier: string;
	businessName: string;
}

const FilterSection = ({
	title,
	onFilterChange,
	selectedOptions,
	onSelectedOrderStatusChange,
	onExportSelectedId,
	showAdminFilters,
	onPendingClicked,
	transportPriceHandler,
	onCreateRefusedSettlement,
	hasSelectedOrders,
	onPrintTickets,
	onPrintList,
	onStatusChange,
	orderStatusChangeToRegisterStatus,
	selectedStatusOrder,
	isSettledOrders = false,
	onPrintHarleyTickets,
	onOrderDelete,
	descriptionDebounceMs = 0,
	showStatusFilter = true,
}: FilterSectionType) => {
	const [zones, setZones] = useState<any>([]);
	const [statusData, setStatusData] = useState<any>([]);
	const [selectedCountry, setSelectedCountry] = useState<any>(null);
	const [selectedCity, setSelectedCity] = useState<any>(null);
	const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
	const [orderId, setOrderId] = useState(null);
	const [orderDescription, setOrderDescription] = useState(null);
	const idTimeout = useRef<any>(null);
	const descriptionTimeout = useRef<any>(null);
	const [fromDate, setFromDate] = useState(null);
	const [toDate, setToDate] = useState(null);
	const [selectedFilterType, setSelectedFilterType] = useState<string>("");
	const [seller, setSeller] = useState([]);
	const [selectedSeller, setSelectedSeller] = useState(null);
	const [selectedStaff, setSelectedStaff] = useState(null);
	const [selectedSettlementValue, setSelectedSettlementValue] =
		useState(null);
	const [staff, setStaff] = useState<any>([]);
	const [orderTypeFilter, setOrderTypeFilter] = useState<string>("");
	const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
	const [selectedFormat, setSelectedFormat] = useState("100x60");
	const { strings } = useLang("filterorder");
	const userRoleId = getUserRoleFromToken();
	const settlementOptions = [
		{ value: true, label: strings?.labels?.settled },
		{ value: false, label: strings?.labels?.unsettled },
	];

	const hasAccessToA4 = hasAccess("A4_TICKETS_ACCESS");
	const hasAccessToHarleyTickets = hasAccess("HARLEY_TICKETS_ACCESS");
	const hasAccessToSellers = hasAccess("ORDER_DETAILS_SELLER_ACCESS");
	const hasAccessToSettlementFilter = hasAccess(
		"ORDER_DETAILS_SETTLEMENT_ACCESS"
	);

	const customOptions = [
		{
			label: strings?.labels?.registration_date,
			value: "registration_date",
		},
		{
			label: strings?.labels?.acceptance_date,
			value: "acceptance_date",
		},
		{
			label: strings?.labels?.transporting_date,
			value: "transporting_date",
		},
		{
			label: strings?.labels?.delivered_date,
			value: "delivered_date",
		},
		{
			label: strings?.labels?.closure_date,
			value: "closure_date",
		},
		{
			label: strings?.labels?.refusal_date,
			value: "refusal_date",
		},
		{
			label: strings?.labels?.collection_date,
			value: "collection_date",
		},
		{
			label: strings?.labels?.returnstorage_date,
			value: "returnstorage_date",
		},
	];

	const typeOptions = [
		{ value: "returnable", label: strings?.returnable },
		{ value: "exchangeable", label: strings?.exchangable },
		{ value: "re_directed", label: strings?.redirected },
	];

	const statusCombination = [
		{
			id: 144,
			label: "Depo internacionale",
			value: "144-20",
		},
		{
			id: 1410,
			label: "Duke u kthyer",
			value: "1410-21",
		},
		{
			id: 22,
			label: "Rinisje Internacionale",
			value: "22-22",
		},
		{
			id: 114,
			label: "Ne Tranzit",
			value: "114-23",
		},
		{
			id: 1110,
			label: "Ndërrim internacional",
			value: "1110-24",
		},
		{
			id: 1810,
			label: "Flete Kthim internacional",
			value: "1810-25",
		},
	];

	// A pending search must not fire after the screen has been left.
	useEffect(
		() => () => {
			clearTimeout(idTimeout.current);
			clearTimeout(descriptionTimeout.current);
		},
		[]
	);

	useEffect(() => {
		fetchZonesCached()
			.then((response: any) => {
				if (Array.isArray(response)) {
					setZones(response);
				} else {
					console.error(
						"Expected an array for zones, but received:",
						response
					);
					setZones([]);
				}
			})
			.catch((error: any) => {
				console.error("Error fetching zones:", error);
				setZones([]);
			});

		fetchStatusCached()
			.then((response: any) => {
				if (Array.isArray(response)) {
					if (isSettledOrders) {
						const settledStatuses = [
							orderStatuses.DELIVERED,
							orderStatuses.RETURNED,
							orderStatuses.REDIRECTED,
							orderStatuses.EXCHANGE_RETURN,
						];
						const test = response
							.filter((item: any) =>
								settledStatuses.includes(item.id)
							)
							.map((status: Status, index: number) => ({
								value: `${status.id}-${index}`,
								label: status.description,
								id: status.id,
							}));
						setStatusData(test);

						if (selectedStatusOrder?.length) {
							setSelectedStatus(
								test
									?.filter((p) =>
										selectedStatusOrder.includes(p.id)
									)
									?.map((p) => p.value) ?? []
							);
						}
					} else {
						const statuses = response.map(
							(status: Status, index: number) => ({
								value: `${status.id}-${index}`,
								label: status.description,
								id: status.id,
							})
						);
						const combinedStatuses = [
							...statuses,
							...statusCombination,
						];

						setStatusData(combinedStatuses);
						if (selectedStatusOrder?.length) {
							setSelectedStatus(
								combinedStatuses
									?.filter((p) =>
										selectedStatusOrder.includes(p.id)
									)
									?.map((p) => p.value) ?? []
							);
						}
					}
				} else {
					console.error(
						"Expected an array for statusData, but received:",
						response
					);
					setStatusData([]);
				}
			})
			.catch((error: any) => {
				console.error("Error fetching status:", error);
				setStatusData([]);
			});
	}, []);

	// Only for the dropdowns this user actually sees (see the JSX below).
	useEffect(() => {
		if (showAdminFilters && hasAccessToSellers) {
			api.get("User/select-small-client-details")
				.then((response: any) => setSeller(response.data ?? []))
				.catch((error: any) => console.log(error));
		}
		if (showAdminFilters) {
			api.get("User/Staff")
				.then((response: any) => setStaff(response.data ?? []))
				.catch((error: any) => console.log(error));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showAdminFilters]);

	const renderCountryOptions = () => {
		const countryOptions = zones
			.filter((country: Zone) => country.levelId === 1)
			.map((country: Zone) => ({
				value: country.id,
				label: country.description,
			}));

		return [...countryOptions];
	};

	const renderCityOptions = () => {
		if (selectedCountry && selectedCountry.length > 0) {
			const cityOptions = zones
				.filter(
					(city: Zone) =>
						city.levelId === 2 &&
						selectedCountry.includes(city.parentId)
				)
				.map((city: Zone) => ({
					value: city.id,
					label: city.description,
				}));

			return [...cityOptions];
		}

		return [];
	};

	// Every change below sends the text typed so far, so a search still waiting to be sent is
	// dropped: sent later, it would bring back the filters from before this change.
	const cancelPendingSearch = () => {
		clearTimeout(idTimeout.current);
		clearTimeout(descriptionTimeout.current);
	};

	const handleCountryChange = (value: any) => {
		cancelPendingSearch();
		setSelectedCountry(value);
		setSelectedCity(null);

		onFilterChange(
			value,
			null,
			selectedStatus,
			orderId,
			orderDescription,
			fromDate,
			toDate,
			selectedFilterType,
			orderTypeFilter,
			selectedSeller,
			selectedStaff,
			selectedSettlementValue
		);
	};

	const handleCityChange = (value: any) => {
		cancelPendingSearch();
		setSelectedCity(value);
		onFilterChange(
			selectedCountry,
			value,
			selectedStatus,
			orderId,
			orderDescription,
			fromDate,
			toDate,
			selectedFilterType,
			orderTypeFilter,
			selectedSeller,
			selectedStaff,
			selectedSettlementValue
		);
	};

	const handleStatusChange = (value: any) => {
		cancelPendingSearch();
		setSelectedStatus(value);
		onFilterChange(
			selectedCountry,
			selectedCity,
			value,
			orderId,
			orderDescription,
			fromDate,
			toDate,
			selectedFilterType,
			orderTypeFilter,
			selectedSeller,
			selectedStaff,
			selectedSettlementValue
		);
	};

	const handleIdChange = (e: any) => {
		setOrderId(e.target.value);
		cancelPendingSearch();
		idTimeout.current = setTimeout(() => {
			onFilterChange(
				selectedCountry,
				selectedCity,
				selectedStatus,
				e.target.value,
				orderDescription,
				fromDate,
				toDate,
				selectedFilterType,
				orderTypeFilter,
				selectedSeller,
				selectedStaff,
				selectedSettlementValue
			);
		}, 1000);
	};

	const handleOrderDescChange = (e: any) => {
		const description = e.target.value;
		setOrderDescription(description);
		const apply = () =>
			onFilterChange(
				selectedCountry,
				selectedCity,
				selectedStatus,
				orderId,
				description,
				fromDate,
				toDate,
				selectedFilterType,
				orderTypeFilter,
				selectedSeller,
				selectedStaff,
				selectedSettlementValue
			);
		cancelPendingSearch();
		if (descriptionDebounceMs > 0) {
			descriptionTimeout.current = setTimeout(
				apply,
				descriptionDebounceMs
			);
		} else {
			apply();
		}
	};

	const handleFromDateChange = (date: any) => {
		cancelPendingSearch();
		setFromDate(date);
		onFilterChange(
			selectedCountry,
			selectedCity,
			selectedStatus,
			orderId,
			orderDescription,
			date,
			toDate,
			selectedFilterType,
			orderTypeFilter,
			selectedSeller,
			selectedStaff,
			selectedSettlementValue
		);
	};

	const handleToDateChange = (date: any) => {
		cancelPendingSearch();
		setToDate(date);
		onFilterChange(
			selectedCountry,
			selectedCity,
			selectedStatus,
			orderId,
			orderDescription,
			fromDate,
			date,
			selectedFilterType,
			orderTypeFilter,
			selectedSeller,
			selectedStaff,
			selectedSettlementValue
		);
	};

	const handleFilterTypeChange = (value: string) => {
		setSelectedFilterType(value);
		if (fromDate || toDate) {
			cancelPendingSearch();
			onFilterChange(
				selectedCountry,
				selectedCity,
				selectedStatus,
				orderId,
				orderDescription,
				fromDate,
				toDate,
				value,
				orderTypeFilter,
				selectedSeller,
				selectedStaff,
				selectedSettlementValue
			);
		}
	};

	const handleSellerChange = (value: any) => {
		cancelPendingSearch();
		setSelectedSeller(value);
		onFilterChange(
			selectedCountry,
			selectedCity,
			selectedStatus,
			orderId,
			orderDescription,
			fromDate,
			toDate,
			selectedFilterType,
			orderTypeFilter,
			value,
			selectedStaff,
			selectedSettlementValue
		);
	};

	const handleStaffChange = (value: any) => {
		cancelPendingSearch();
		setSelectedStaff(value);
		onFilterChange(
			selectedCountry,
			selectedCity,
			selectedStatus,
			orderId,
			orderDescription,
			fromDate,
			toDate,
			selectedFilterType,
			orderTypeFilter,
			selectedSeller,
			value,
			selectedSettlementValue
		);
	};

	const handleSettlementTypeChange = (value: any) => {
		cancelPendingSearch();
		setSelectedSettlementValue(value);
		onFilterChange(
			selectedCountry,
			selectedCity,
			selectedStatus,
			orderId,
			orderDescription,
			fromDate,
			toDate,
			selectedFilterType,
			orderTypeFilter,
			selectedSeller,
			selectedStaff,
			value
		);
	};

	const handleTypeChange = (value: string) => {
		cancelPendingSearch();
		setOrderTypeFilter(value);
		onFilterChange(
			selectedCountry,
			selectedCity,
			selectedStatus,
			orderId,
			orderDescription,
			fromDate,
			toDate,
			selectedFilterType,
			value,
			selectedSeller,
			selectedStaff,
			selectedSettlementValue
		);
	};

	const currentLang = useSelector(
		(state: RootState) => state.langReducer.lang
	);

	const mapStatusDataToOptions = () => {
		const macedonianTranslations: { [key: number]: string } = {
			1: "Регистриран",
			2: "Подготвено за собирање",
			3: "Собрано",
			4: "Во магацин",
			5: "Во транспорт",
			6: "Доставени",
			7: "Одбиено",
			8: "Во чекање",
			9: "Во чекање за подигање",
			10: "Вратено",
			11: "Промена",
			12: "Повторно започнато",
			13: "Во магацин за враќање",
			14: "Меѓународен транспорт",
			15: "Промена при враќање",
		};

		return statusData.map((status: Status) => {
			const translatedLabel =
				currentLang === "mk" ? macedonianTranslations[status.id] : null;
			return {
				value: status.id,
				label: translatedLabel || status.description,
			};
		});
	};

	const renderSellerOptions = () => {
		return seller.map((sellerItem: Seller, index: number) => ({
			value: `${sellerItem.id}-${index}`,
			label: `${sellerItem.username}  (${
				sellerItem.uniqueSellerIdentifier ?? ""
			})`,
		}));
	};

	const renderStaffOptions = () => {
		const staffOptions = staff
			.filter((item: any) => item.userRoleId !== userRoles.SUBCLIENT)
			.map((staffItem: any, index: number) => ({
				value: `${staffItem.id}-${index}`,
				label: `${staffItem.firstName} ${staffItem.lastName} (${staffItem.userRole})`,
			}));

		return [...staffOptions];
	};

	const [selectValue, setSelectValue] = useState(null);

	const handlePrintModalOk = () => {
		setIsPrintModalOpen(false);
		onPrintTickets(selectedFormat);
	};

	const handlePrintModalCancel = () => {
		setIsPrintModalOpen(false);
	};

	return (
		<>
			<div className={classes.container}>
				<h1 className={classes.filterOrderTitle}>{title}</h1>
				<div className={classes.inputs}>
					<DefaultInput
						placeholder={strings?.searchcode}
						value={orderId}
						onChange={handleIdChange}
						allowClear
						label={strings?.searchcode}
						prefix={<CodeIcon />}
						className={classes.input}
					/>
					<DefaultInput
						placeholder={strings?.searchtext}
						value={orderDescription}
						onChange={handleOrderDescChange}
						allowClear
						label={strings?.searchtext}
						prefix={<SearchIcon />}
						className={classes.input}
					/>
					{showStatusFilter && (
						<DefaultSelect
							label={strings?.status}
							placeholder={strings?.chooseStatus}
							options={statusData}
							value={selectedStatus ?? undefined}
							onChange={handleStatusChange}
							allowClear
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							mode="multiple"
						/>
					)}

					<DefaultSelect
						label={strings?.filterdate}
						placeholder={strings?.choose}
						options={customOptions}
						onChange={handleFilterTypeChange}
						flexDirection="col"
						alignCenter="start"
						className={classes.input}
						value={selectedFilterType}
						allowClear
					/>
					<div className={classes.datat}>
						<DefaultDatePicker
							label={strings?.fromdate}
							value={fromDate}
							onChange={handleFromDateChange}
							width={172}
							className={classes.firstData}
						/>
						<DefaultDatePicker
							label={strings?.untill}
							value={toDate}
							onChange={handleToDateChange}
							width={172}
							className={classes.secondData}
						/>
					</div>
					{showAdminFilters && (
						<DefaultSelect
							label={strings?.responsible}
							placeholder={strings?.responsible}
							options={renderStaffOptions()}
							onChange={handleStaffChange}
							value={selectedStaff ?? undefined}
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							allowClear
							mode="multiple"
						/>
					)}
					<DefaultSelect
						label={strings?.country}
						placeholder={strings?.choose}
						options={renderCountryOptions()}
						value={selectedCountry ?? undefined}
						onChange={handleCountryChange}
						allowClear
						flexDirection="col"
						alignCenter="start"
						className={classes.input}
						mode="multiple"
					/>
					<DefaultSelect
						label={strings?.city}
						placeholder={strings?.choose}
						options={renderCityOptions()}
						value={selectedCity ?? undefined}
						onChange={handleCityChange}
						allowClear
						flexDirection="col"
						alignCenter="start"
						className={classes.input}
						mode="multiple"
					/>
					{hasAccessToSettlementFilter && (
						<DefaultSelect
							label={strings?.settlement}
							placeholder={strings?.settelementType}
							options={settlementOptions}
							value={selectedSettlementValue}
							onChange={handleSettlementTypeChange}
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							allowClear
						/>
					)}
					{showAdminFilters && hasAccessToSellers && (
						<DefaultSelect
							label={strings?.seller}
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							placeholder={strings?.chooseseller}
							options={renderSellerOptions()}
							value={selectedSeller ?? undefined}
							onChange={handleSellerChange}
							allowClear
							mode="multiple"
						/>
					)}
					<DefaultSelect
						placeholder={strings?.options}
						label={strings?.options}
						options={selectedOptions?.filter(
							(option: any) => option.show !== false
						)}
						onChange={(value: any) => {
							if (!value) return;
							if (value === "ready") {
								onSelectedOrderStatusChange();
							} else if (value === "export_excel") {
								onExportSelectedId();
							} else if (value === "pending") {
								onPendingClicked();
							} else if (value === "print_ticket") {
								setIsPrintModalOpen(true);
							} else if (value === "changeTransportPrice") {
								if (hasSelectedOrders) {
									transportPriceHandler();
								} else if (!hasSelectedOrders) {
									toast.error(
										"Nuk keni selektuar asnje porosi"
									);
									value = null;
								}
							} else if (value === "refusedSettlement") {
								onCreateRefusedSettlement();
							} else if (value === "print_list") {
								onPrintList();
							} else if (value === "statusChange") {
								onStatusChange();
							} else if (value === "register_status") {
								orderStatusChangeToRegisterStatus();
							} else if (value === "harley_ticket") {
								onPrintHarleyTickets();
							} else if (value === "deleteOrders") {
								onOrderDelete();
							}
						}}
						flexDirection="col"
						alignCenter="start"
						className={classes.input}
						allowClear
					/>
					<DefaultSelect
						label={strings?.orderType}
						placeholder={strings?.choose}
						options={typeOptions}
						value={orderTypeFilter}
						onChange={handleTypeChange}
						allowClear
						flexDirection="col"
						alignCenter="start"
						className={classes.input}
					/>
				</div>
			</div>
			<Modal
				title="Zgjidh Formatin e Printimit"
				open={isPrintModalOpen}
				onOk={handlePrintModalOk}
				onCancel={handlePrintModalCancel}
				footer={
					<Buttons
						button1Text="Anulo"
						button2Text="Printo"
						onButton1Click={handlePrintModalCancel}
						onButton2Click={handlePrintModalOk}
					/>
				}
			>
				<Radio.Group
					value={selectedFormat}
					onChange={(e) => setSelectedFormat(e.target.value)}
				>
					<Radio value="100x60">100x60</Radio>

					{hasAccessToA4 && <Radio value="A4">A4</Radio>}
					{hasAccessToHarleyTickets && (
						<Radio value="harleyTicket">Harley tiket</Radio>
					)}
				</Radio.Group>
			</Modal>
		</>
	);
};

export default FilterSection;
